/**
 * CHATTEN PÅ BOOKINGEN (PERSONTYPER-BESTILLING §2.3, migrering 1105/1106).
 *
 * Én tråd per booking. Før den åpnes, leser byrået og motparten (festival, arrangør, spillested).
 * «Åpne for reisefølget» gjør byrået eller den som har Hele artisten + økonomi; det kan ikke angres.
 * Reisefølget leser fra sitt eget tidspunkt (`visibleFrom`), og den med Hele artisten + økonomi ser
 * hele historikken. Alt avgjøres i basen (`app.booking_chat_state`); dette er bare formen.
 *
 * Samtalen selv er den vanlige chatmotoren, adressert med tråden (`/v1/booking-chat-thread`), så
 * reisefølget kan lese og skrive fra sitt eget hus.
 */
import { z } from 'zod';

import {
  createPerformanceChatMessageRequestSchema,
  listPerformanceChatMentionsQuerySchema,
  listPerformanceChatMessagesQuerySchema,
  markPerformanceChatReadRequestSchema,
  performanceChatDtoSchema,
} from './performance-chat.js';

/** Bookingene i Mine konserter: programposten med delt advance, forespørselen, byråets booking. */
export const BOOKING_CHAT_TARGET_KINDS = ['performance', 'enquiry', 'agency_booking'] as const;
export const bookingChatTargetKindSchema = z.enum(BOOKING_CHAT_TARGET_KINDS);
export type BookingChatTargetKind = z.infer<typeof bookingChatTargetKindSchema>;

/** Hvem leseren er i samtalen: byrået, motparten, eller reisefølget. */
export const BOOKING_CHAT_SIDES = ['agency', 'counterparty', 'party'] as const;
export const bookingChatSideSchema = z.enum(BOOKING_CHAT_SIDES);
export type BookingChatSide = z.infer<typeof bookingChatSideSchema>;

export const bookingChatReaderSchema = z
  .object({
    personId: z.uuid(),
    name: z.string(),
    /** Organisasjonen personen leser gjennom. Null for en i reisefølget uten organisasjon. */
    organizationName: z.string().nullable(),
    side: bookingChatSideSchema,
  })
  .meta({ id: 'BookingChatReader' });
export type BookingChatReader = z.infer<typeof bookingChatReaderSchema>;

export const bookingChatAccessSchema = z
  .object({
    /** Null til tråden finnes (byråets egen booking før noen har skrevet). */
    threadId: z.uuid().nullable(),
    side: bookingChatSideSchema,
    /** Byråets booking. Null for motparten, som har sine egne id-er. */
    bookingKind: z.enum(['enquiry', 'agency_booking']).nullable(),
    bookingId: z.uuid().nullable(),
    /** Når chatten ble åpnet for reisefølget, og av hvem. Null mens den er stengt. */
    openedAt: z.iso.datetime({ offset: true }).nullable(),
    openedByName: z.string().nullable(),
    openedByOrganizationName: z.string().nullable(),
    /** Byrået, eller Hele artisten + økonomi, før den er åpnet. Motparten aldri. */
    canOpen: z.boolean(),
    /** Ser hele historikken (byrået, motparten, Hele artisten + økonomi). */
    fullHistory: z.boolean(),
    /** Reisefølget: tidspunktet leseren ser fra. Null når hen ser alt. */
    visibleFrom: z.iso.datetime({ offset: true }).nullable(),
    /** Hvem som leser, med navn og organisasjon. */
    readers: z.array(bookingChatReaderSchema),
  })
  .meta({ id: 'BookingChatAccess' });
export type BookingChatAccess = z.infer<typeof bookingChatAccessSchema>;

export const bookingChatTargetQuerySchema = z
  .object({ kind: bookingChatTargetKindSchema, id: z.uuid() })
  .meta({ id: 'BookingChatTarget' });
export type BookingChatTarget = z.infer<typeof bookingChatTargetQuerySchema>;

/** «Krever handling»: bekreftede bookinger innen terskelen der chatten fortsatt er stengt. */
export const bookingChatClosedSoonItemSchema = z
  .object({
    kind: z.enum(['enquiry', 'agency_booking']),
    bookingId: z.uuid(),
    startsOn: z.iso.date(),
    title: z.string(),
    daysLeft: z.number().int(),
  })
  .meta({ id: 'BookingChatClosedSoonItem' });
export type BookingChatClosedSoonItem = z.infer<typeof bookingChatClosedSoonItemSchema>;

export const bookingChatClosedSoonListSchema = z
  .object({
    items: z.array(bookingChatClosedSoonItemSchema),
    /** Terskelen i dager (bookinginnstillingene, standard 14). */
    reminderDays: z.number().int(),
  })
  .meta({ id: 'BookingChatClosedSoonList' });
export type BookingChatClosedSoonList = z.infer<typeof bookingChatClosedSoonListSchema>;

/** Standard terskel for påminnelsen. Endres per organisasjon (Innstillinger › Organisasjon). */
export const DEFAULT_BOOKING_CHAT_REMINDER_DAYS = 14;

export const bookingChatSettingsSchema = z
  .object({
    /** Dager før konserten «Krever handling» minner om en stengt chat. */
    reminderDays: z.number().int().min(1).max(365),
  })
  .meta({ id: 'BookingChatSettings' });
export type BookingChatSettings = z.infer<typeof bookingChatSettingsSchema>;

/* ------------------------------------------------- samtalen, adressert med tråden */

export const bookingThreadChatDtoSchema = performanceChatDtoSchema
  .omit({ performanceId: true })
  .meta({ id: 'BookingThreadChatDto' });

export const listBookingThreadChatMessagesQuerySchema = listPerformanceChatMessagesQuerySchema
  .omit({ performanceId: true })
  .extend({ threadId: z.uuid() })
  .meta({ id: 'ListBookingThreadChatMessagesQuery' });

export const createBookingThreadChatMessageRequestSchema = createPerformanceChatMessageRequestSchema
  .omit({ performanceId: true })
  .extend({ threadId: z.uuid() })
  .meta({ id: 'CreateBookingThreadChatMessageRequest' });

export const markBookingThreadChatReadRequestSchema = markPerformanceChatReadRequestSchema
  .omit({ performanceId: true })
  .extend({ threadId: z.uuid() })
  .meta({ id: 'MarkBookingThreadChatReadRequest' });

export const listBookingThreadChatMentionsQuerySchema = listPerformanceChatMentionsQuerySchema
  .omit({ performanceId: true })
  .extend({ threadId: z.uuid() })
  .meta({ id: 'ListBookingThreadChatMentionsQuery' });

export type BookingThreadChatDto = z.infer<typeof bookingThreadChatDtoSchema>;
export type ListBookingThreadChatMessagesQuery = z.infer<
  typeof listBookingThreadChatMessagesQuerySchema
>;
export type CreateBookingThreadChatMessageRequest = z.infer<
  typeof createBookingThreadChatMessageRequestSchema
>;
export type MarkBookingThreadChatReadRequest = z.infer<typeof markBookingThreadChatReadRequestSchema>;
export type ListBookingThreadChatMentionsQuery = z.infer<
  typeof listBookingThreadChatMentionsQuerySchema
>;
